import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { CBMError, Maintenance, User } from 'app/models';
import { ContractType } from 'app/models/enums';
import { AuthorizationService, MaintenanceService, OAuthService, UserService } from 'app/services';
import { DialogReportServicedeskComponent } from 'app/shared/dialog-report-servicedesk/dialog-report-servicedesk.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(DialogReportServicedeskComponent) reportDialogComponent: DialogReportServicedeskComponent;

  public changeLanguageTitle: string;

  private eventSubscription: any;

  private maintenance: Maintenance;

  public languages = [
    { shortname: 'nl', name: 'Nederlands', active: true },
    { shortname: 'gb', name: 'Engels', active: false }
  ];

  constructor(
    private readonly authorizationService: AuthorizationService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly translate: TranslateService,
    private readonly oauthService: OAuthService,
    private readonly maintenanceService: MaintenanceService,
  ) {
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('nl');
  }

  ngOnInit() {
    this.retrieveLoggedInUser();
    this.initialiseChangeLanguageTitle();
    this.getMaintenance();
  }

  ngOnDestroy() {
    this.eventSubscription.unsubscribe();
  }

  private getMaintenance(): void {
    this.maintenanceService.getMaintenance().subscribe(
      response => {
        this.maintenance = response;
      },
      (errors: CBMError[]) => console.log(errors)
    );
  }

  private initialiseChangeLanguageTitle() {
    this.translateService.get('HEADER.SELECTLANGUAGE').subscribe(translation => {
      this.changeLanguageTitle = translation;
    });
  }

  private retrieveLoggedInUser(): void {
    if (!this.oauthService.isLoggedIn()) {
      return;
    }

    // No ASync HTTP calls should be used when processing the keycloak code to access token exchange or else
    // a second redirect to the login page will be triggered
    if (!this.isKeycloakLoginCallback()) {
      this.userService.getLoggedInUser().subscribe(
        user => {
          if (user) {
            this.authorizationService.setAuthenticatedUserAndRoles(user, this.oauthService.getRoles());

            const userLanguage = this.languages.find(language => language.shortname === user.language);
            this.changeLanguage(userLanguage);
          }
        },
        error => console.error('error: ', error)
      );
    }
  }

  public isKeycloakLoginCallback(): boolean {
    return window.location.pathname.startsWith('/keycloak-login-callback');
  }

  public changeLanguage(selectedLanguage) {
    this.languages.map(language => (language.active = false));
    selectedLanguage.active = true;

    this.translate.use(selectedLanguage.shortname);

    this.initialiseChangeLanguageTitle();
  }

  public getFlagClass(language) {
    return `flag-icon-${language.shortname}`;
  }

  public linkToHome(): string {
    if (!this.authorizationService.hasLoginUser()) {
      return 'login';
    }
    return 'contracts';
  }

  public logout() {
    this.oauthService.logout();
  }

  public displayContractsMenuItem(): boolean {
    return this.isLocationAdmin() || this.authorizationService.getContracts().length > 1;
  }

  public displayServiceDeskMenuItem(): boolean {
    return !this.isLocationAdmin() && !this.isMapAdmin() && !this.isApplicationAdmin();
  }

  public displayFcdUserManualItem(): boolean {
    return this.displayServiceDeskMenuItem() && this.userHasFcdContract();
  }

  private userHasFcdContract(): boolean {
    return this.authorizationService.getContracts().some(contract => contract.contractType === ContractType.FCD);
  }

  public isLocationAdmin(): boolean {
    return this.authorizationService.isLocationAdmin();
  }

  public isMapAdmin(): boolean {
    return this.authorizationService.isMapAdmin();
  }

  public isLocationOrMapAdmin(): boolean {
    return this.authorizationService.isLocationOrMapAdmin();
  }

  public isApplicationAdmin(): boolean {
    return this.authorizationService.isApplicationAdmin();
  }

  public getUser(): User {
    return this.authorizationService.getUser();
  }

  public showReportDialog(): void {
    this.reportDialogComponent.show();
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts